
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {AccountWithAccountMargin} from '../core/AccountWithAccountMargin';
import {FieldModelAccountWithAccountMargin} from '../core/AccountWithAccountMargin';

/**
 * AccountsGetPageResponse struct
 */
class AccountsGetPageResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!Array=} accountWithAccountMargins
   * @param {!number=} recordsTotal
   * @param {!number=} pageSize
   * @param {!number=} pageNumberZeroBased
   * @param {!boolean=} hasPersonalDetails
   * @constructor
   */
  constructor (parent = new Response(), accountWithAccountMargins = [], recordsTotal = new UInt64(0, 0), pageSize = new UInt64(0, 0), pageNumberZeroBased = new UInt64(0, 0), hasPersonalDetails = false) {
    super()
    super.copy(parent)
    this.AccountWithAccountMargins = accountWithAccountMargins
    this.RecordsTotal = recordsTotal
    this.PageSize = pageSize
    this.PageNumberZeroBased = pageNumberZeroBased
    this.HasPersonalDetails = hasPersonalDetails
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountsGetPageResponse}
   * @param {!AccountsGetPageResponse} other Other struct
   * @returns {!AccountsGetPageResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.AccountWithAccountMargins != null) {
      this.AccountWithAccountMargins = []
      for (let item of other.AccountWithAccountMargins) {
        if (item != null) {
          let tempItem
          tempItem = AccountWithAccountMargin.fromObject(item)
          this.AccountWithAccountMargins.push(tempItem)
        } else {
          this.AccountWithAccountMargins.push(null)
        }
      }
    } else {
      this.AccountWithAccountMargins = null
    }
    if (other.RecordsTotal != null) {
      this.RecordsTotal = UInt64.fromNumber(other.RecordsTotal)
    } else {
      this.RecordsTotal = null
    }
    if (other.PageSize != null) {
      this.PageSize = UInt64.fromNumber(other.PageSize)
    } else {
      this.PageSize = null
    }
    if (other.PageNumberZeroBased != null) {
      this.PageNumberZeroBased = UInt64.fromNumber(other.PageNumberZeroBased)
    } else {
      this.PageNumberZeroBased = null
    }
    if (other.HasPersonalDetails != null) {
      this.HasPersonalDetails = other.HasPersonalDetails
    } else {
      this.HasPersonalDetails = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountsGetPageResponse}
   * @returns {!AccountsGetPageResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountsGetPageResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountsGetPageResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountsGetPageResponse}
   * @param {!AccountsGetPageResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountsGetPageResponse)) {
      throw new TypeError('Instance of AccountsGetPageResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountsGetPageResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      AccountWithAccountMargins: ((this.AccountWithAccountMargins != null) ? Array.from(this.AccountWithAccountMargins, item => ((item != null) ? item : null)) : null),
      RecordsTotal: ((this.RecordsTotal != null) ? this.RecordsTotal.toNumber() : null),
      PageSize: ((this.PageSize != null) ? this.PageSize.toNumber() : null),
      PageNumberZeroBased: ((this.PageNumberZeroBased != null) ? this.PageNumberZeroBased.toNumber() : null),
      HasPersonalDetails: ((this.HasPersonalDetails != null) ? this.HasPersonalDetails : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountsGetPageResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountsGetPageResponse} other Object value
   * @returns {!AccountsGetPageResponse} Created struct
   */
  static fromObject (other) {
    return new AccountsGetPageResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountsGetPageResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountsGetPageResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountsGetPageResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6465
  }
}

export { AccountsGetPageResponse };

/**
 * Fast Binary Encoding AccountsGetPageResponse field model
 */
class FieldModelAccountsGetPageResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.AccountWithAccountMargins = new fbe.FieldModelVector(new FieldModelAccountWithAccountMargin(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.RecordsTotal = new fbe.FieldModelUInt64(buffer, this.AccountWithAccountMargins.fbeOffset + this.AccountWithAccountMargins.fbeSize)
    this.PageSize = new fbe.FieldModelUInt64(buffer, this.RecordsTotal.fbeOffset + this.RecordsTotal.fbeSize)
    this.PageNumberZeroBased = new fbe.FieldModelUInt64(buffer, this.PageSize.fbeOffset + this.PageSize.fbeSize)
    this.HasPersonalDetails = new fbe.FieldModelBool(buffer, this.PageNumberZeroBased.fbeOffset + this.PageNumberZeroBased.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelAccountsGetPageResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountsGetPageResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountsGetPageResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.AccountWithAccountMargins.fbeSize + this.RecordsTotal.fbeSize + this.PageSize.fbeSize + this.PageNumberZeroBased.fbeSize + this.HasPersonalDetails.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountsGetPageResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.AccountWithAccountMargins.fbeExtra + this.RecordsTotal.fbeExtra + this.PageSize.fbeExtra + this.PageNumberZeroBased.fbeExtra + this.HasPersonalDetails.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountsGetPageResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountsGetPageResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountsGetPageResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6465
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountsGetPageResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountsGetPageResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccountWithAccountMargins.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountWithAccountMargins.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountWithAccountMargins.fbeSize

    if ((fbeCurrentSize + this.RecordsTotal.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RecordsTotal.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RecordsTotal.fbeSize

    if ((fbeCurrentSize + this.PageSize.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PageSize.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageSize.fbeSize

    if ((fbeCurrentSize + this.PageNumberZeroBased.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PageNumberZeroBased.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageNumberZeroBased.fbeSize

    if ((fbeCurrentSize + this.HasPersonalDetails.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.HasPersonalDetails.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.HasPersonalDetails.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountsGetPageResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountsGetPageResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountsGetPageResponse}
   * @param {!AccountsGetPageResponse} fbeValue Default value, defaults is new AccountsGetPageResponse()
   * @returns {!AccountsGetPageResponse} AccountsGetPageResponse value
   */
  get (fbeValue = new AccountsGetPageResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountsGetPageResponse}
   * @param {!AccountsGetPageResponse} fbeValue AccountsGetPageResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccountWithAccountMargins.fbeSize) <= fbeStructSize) {
      this.AccountWithAccountMargins.get(fbeValue.AccountWithAccountMargins)
    } else {
      fbeValue.AccountWithAccountMargins.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountWithAccountMargins.fbeSize

    if ((fbeCurrentSize + this.RecordsTotal.fbeSize) <= fbeStructSize) {
      fbeValue.RecordsTotal = this.RecordsTotal.get(new UInt64(0, 0))
    } else {
      fbeValue.RecordsTotal = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RecordsTotal.fbeSize

    if ((fbeCurrentSize + this.PageSize.fbeSize) <= fbeStructSize) {
      fbeValue.PageSize = this.PageSize.get(new UInt64(0, 0))
    } else {
      fbeValue.PageSize = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageSize.fbeSize

    if ((fbeCurrentSize + this.PageNumberZeroBased.fbeSize) <= fbeStructSize) {
      fbeValue.PageNumberZeroBased = this.PageNumberZeroBased.get()
    } else {
      fbeValue.PageNumberZeroBased = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageNumberZeroBased.fbeSize

    if ((fbeCurrentSize + this.HasPersonalDetails.fbeSize) <= fbeStructSize) {
      fbeValue.HasPersonalDetails = this.HasPersonalDetails.get(false)
    } else {
      fbeValue.HasPersonalDetails = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.HasPersonalDetails.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountsGetPageResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountsGetPageResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountsGetPageResponse}
   * @param {!AccountsGetPageResponse} fbeValue AccountsGetPageResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountsGetPageResponse}
   * @param {AccountsGetPageResponse} fbeValue AccountsGetPageResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.AccountWithAccountMargins.set(fbeValue.AccountWithAccountMargins)
    this.RecordsTotal.set(fbeValue.RecordsTotal)
    this.PageSize.set(fbeValue.PageSize)
    this.PageNumberZeroBased.set(fbeValue.PageNumberZeroBased)
    this.HasPersonalDetails.set(fbeValue.HasPersonalDetails)
  }
}

export { FieldModelAccountsGetPageResponse };

/**
 * Fast Binary Encoding AccountsGetPageResponse model
 */
class AccountsGetPageResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountsGetPageResponse(this.buffer, 4)
  }

  /**
   * Get the AccountsGetPageResponse model
   * @this {!AccountsGetPageResponseModel}
   * @returns {!FieldModelAccountsGetPageResponse} model AccountsGetPageResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountsGetPageResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountsGetPageResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountsGetPageResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountsGetPageResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountsGetPageResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountsGetPageResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountsGetPageResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountsGetPageResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountsGetPageResponseModel}
   * @param {!AccountsGetPageResponse} value AccountsGetPageResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountsGetPageResponseModel}
   * @param {!AccountsGetPageResponse} value AccountsGetPageResponse value, defaults is new AccountsGetPageResponse()
   * @return {!object} Deserialized AccountsGetPageResponse value and its size
   */
  deserialize (value = new AccountsGetPageResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountsGetPageResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountsGetPageResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountsGetPageResponseModel}
   * @param {!number} prev Previous AccountsGetPageResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountsGetPageResponseModel };
